/**
 * AuthGate — app-wide "log in / sign up to continue" prompt.
 *
 * `requireAuth(action?)` runs the action when authenticated, otherwise opens the
 * modal and returns false, so callers can guard interactions on public pages.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type PropsWithChildren,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@wlb/shared/context/AuthContext';
import { useTranslation } from '@wlb/shared/i18n/useTranslation';
import { Button } from '../ui/Button';
import { BrandLogo } from '../ui/BrandLogo';
import { CloseIcon } from '../ui/icons';
import authGateHero from '../../assets/images/auth-gate-hero.png';
import './authGate.css';

interface AuthGateValue {
  requireAuth: (action?: () => void) => boolean;
  openGate: () => void;
}

const AuthGateContext = createContext<AuthGateValue | null>(null);

export const useAuthGate = (): AuthGateValue => {
  const ctx = useContext(AuthGateContext);
  if (!ctx) {
    throw new Error('useAuthGate must be used inside <AuthGateProvider>');
  }
  return ctx;
};

export function AuthGateProvider({ children }: PropsWithChildren) {
  const { isAuthenticated } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const dialogRef = useRef<HTMLDivElement>(null);
  const returnFocusRef = useRef<HTMLElement | null>(null);

  const openGate = useCallback(() => {
    returnFocusRef.current = document.activeElement as HTMLElement | null;
    setOpen(true);
  }, []);

  const closeGate = useCallback(() => {
    setOpen(false);
    returnFocusRef.current?.focus?.();
  }, []);

  const requireAuth = useCallback(
    (action?: () => void): boolean => {
      if (isAuthenticated) {
        action?.();
        return true;
      }
      openGate();
      return false;
    },
    [isAuthenticated, openGate],
  );

  useEffect(() => {
    if (!open) {
      return;
    }
    dialogRef.current?.focus();
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closeGate();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, closeGate]);

  const go = (path: string) => {
    closeGate();
    navigate(path);
  };

  return (
    <AuthGateContext.Provider value={{ requireAuth, openGate }}>
      {children}
      {open && (
        <div className="auth-gate__overlay" onClick={closeGate}>
          <div
            ref={dialogRef}
            className="auth-gate__dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="auth-gate-title"
            tabIndex={-1}
            onClick={event => event.stopPropagation()}
          >
            <div className="auth-gate__hero">
              <img
                className="auth-gate__hero-image"
                src={authGateHero}
                alt=""
                aria-hidden
              />
              <BrandLogo compact className="auth-gate__logo" />
              <button
                className="auth-gate__close"
                onClick={closeGate}
                aria-label={t('common.buttons.close')}
              >
                <CloseIcon size={24} />
              </button>
            </div>
            <div className="auth-gate__content">
              <div className="auth-gate__copy">
                <h2 id="auth-gate-title" className="auth-gate__title">
                  {t('auth.gate.title')}
                </h2>
                <p className="auth-gate__message">{t('auth.gate.message')}</p>
              </div>
              <div className="auth-gate__actions">
                <Button size="md" variant="ghost" fullWidth onClick={closeGate}>
                  {t('common.buttons.cancel')}
                </Button>
                <Button size="md" fullWidth onClick={() => go('/signup')}>
                  {t('auth.gate.signUp')}
                </Button>
              </div>
              <p className="auth-gate__login">
                {t('auth.gate.alreadyHaveAccount')}{' '}
                <button type="button" onClick={() => go('/login')}>
                  {t('auth.gate.logIn')}
                </button>
              </p>
            </div>
          </div>
        </div>
      )}
    </AuthGateContext.Provider>
  );
}
